import { Analytics } from '@cobliteam/analytics';
import { useGetDevices } from '@cobliteam/hooks/src/useEntity/useDevices';
import useFuscaVehicle from '@cobliteam/hooks/src/useEntity/useFuscaVehicles';
import useVehicle from '@cobliteam/hooks/src/useEntity/useVehicle';
import { useTranslation } from '@cobliteam/i18n';
import Button from '@cobliteam/ui/components/Button';
import Typography from '@cobliteam/ui/components/Typography';
import Select, { SelectContent, SelectItem, SelectTrigger, SelectValue } from '@cobliteam/ui/form/Select';
import { formatVehicleLabel } from '@cobliteam/ui-business/components/Selects/SelectVehicle/utils';
import { Check } from 'lucide-react';
import { useMemo, useState } from 'react';

interface DeviceAssociationStepBodyProps {
  fleetId?: string;
  /** Called after the device is associated, to advance to the next step. */
  onCompleted?: () => void;
}

/** Linger after success so the user sees the confirmation before advancing. */
const ADVANCE_DELAY_MS = 900;

/**
 * Inline "associate device to vehicle" — pick a vehicle without a device and an unassigned device.
 * Both pickers are the same dropdown (button) control so the pair reads consistently.
 */
const DeviceAssociationStepBody = ({ fleetId, onCompleted }: DeviceAssociationStepBodyProps) => {
  const { t } = useTranslation('basic-value-onboarding');
  const [vehicleId, setVehicleId] = useState<string>('');
  const [deviceId, setDeviceId] = useState<string>('');
  const { data: vehicles } = useVehicle().getVehicles({});
  const { data: devices } = useGetDevices({ includeLastLocation: false, includeLastMovingStateTime: false });
  const availableVehicles = useMemo(() => (vehicles ?? []).filter((vehicle) => !vehicle.deviceId), [vehicles]);
  const unassignedDevices = useMemo(() => (devices ?? []).filter((device) => !device.vehicle), [devices]);
  const { mutateAsync: associateDevice, isPending, isSuccess } = useFuscaVehicle().associateDevice;

  const handleAssociate = async () => {
    if (!(vehicleId && deviceId)) return;
    await associateDevice({ vehicleId, deviceId });
    Analytics.track('BasicValueModalItemCreated', { fleetId, feature: 'device-association' });
    window.setTimeout(() => onCompleted?.(), ADVANCE_DELAY_MS);
  };

  if (isSuccess) {
    return (
      <Typography variant='text-sm' className='flex items-center gap-1 text-success'>
        <Check size={16} aria-hidden />
        {t('inline.device_associated')}
      </Typography>
    );
  }

  return (
    <div className='flex flex-col gap-2'>
      <Select value={vehicleId} onValueChange={setVehicleId}>
        <SelectTrigger>
          <SelectValue placeholder={t('inline.select_vehicle')} />
        </SelectTrigger>
        <SelectContent portal={false}>
          {availableVehicles.map((vehicle) => (
            <SelectItem key={vehicle.id} value={vehicle.id}>
              {formatVehicleLabel(vehicle)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <div className='flex items-end gap-2'>
        <Select value={deviceId} onValueChange={setDeviceId}>
          <SelectTrigger className='flex-1'>
            <SelectValue placeholder={t('inline.select_device')} />
          </SelectTrigger>
          <SelectContent portal={false}>
            {unassignedDevices.map((device) => (
              <SelectItem key={device.id} value={device.id}>
                {device.cobliId}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button loading={isPending} disabled={!(vehicleId && deviceId)} onClick={handleAssociate}>
          {t('associate')}
        </Button>
      </div>
    </div>
  );
};

export default DeviceAssociationStepBody;
